

export type ElementType = 'fire' | 'earth' | 'air' | 'water';

export interface ElementConfig {
    id: ElementType;
    label: string;
    colors: {
        primary: string;
        dark: string;
        text: string;
        border: string;
    };
}

export const ELEMENTS: Record<ElementType, ElementConfig> = {
    fire: {
        id: 'fire',
        label: 'אש',
        colors: {
            primary: '#ef4444', // red-500
            dark: '#7f1d1d',    // red-900
            text: 'text-red-500',
            border: 'border-red-500/30'
        }
    },
    earth: {
        id: 'earth',
        label: 'אדמה',
        colors: {
            primary: '#5e4238', // Brown 600
            dark: '#492f2b',    // Brown 900
            text: 'text-[#A1887F]',
            border: 'border-[#6D4C41]/30'
        }
    },
    air: {
        id: 'air',
        label: 'אוויר',
        colors: {
            primary: '#f8fafc', // slate-50 (was slate-200 #e2e8f0) - Brightened for cleaner gradients
            dark: '#c6e0ffff',    // blue-400
            text: 'text-blue-200',
            border: 'border-blue-200/30'
        }
    },
    water: {
        id: 'water',
        label: 'מים',
        colors: {
            primary: '#2275fa', // blue-500
            dark: '#172554',    // blue-950
            text: 'text-blue-400',
            border: 'border-blue-400/30'
        }
    }
};

export interface SignComposition {
    primary: ElementType;
    primaryPercentage: number;
    secondary?: ElementType;
    secondaryPercentage?: number;
}

export const SIGN_COMPOSITIONS: Record<number, SignComposition> = {
    1: { primary: 'fire', primaryPercentage: 100 }, // Aries
    2: { primary: 'earth', primaryPercentage: 75, secondary: 'water', secondaryPercentage: 25 }, // Taurus
    3: { primary: 'air', primaryPercentage: 60, secondary: 'fire', secondaryPercentage: 40 }, // Gemini
    4: { primary: 'water', primaryPercentage: 60, secondary: 'earth', secondaryPercentage: 40 }, // Cancer
    5: { primary: 'fire', primaryPercentage: 70, secondary: 'air', secondaryPercentage: 30 }, // Leo
    6: { primary: 'earth', primaryPercentage: 100 }, // Virgo
    7: { primary: 'air', primaryPercentage: 75, secondary: 'water', secondaryPercentage: 25 }, // Libra
    8: { primary: 'water', primaryPercentage: 75, secondary: 'fire', secondaryPercentage: 25 }, // Scorpio
    9: { primary: 'fire', primaryPercentage: 80, secondary: 'water', secondaryPercentage: 20 }, // Sagittarius
    10: { primary: 'earth', primaryPercentage: 70, secondary: 'air', secondaryPercentage: 30 }, // Capricorn
    11: { primary: 'air', primaryPercentage: 100 }, // Aquarius
    12: { primary: 'water', primaryPercentage: 100 } // Pisces
};

export const getSignElementData = (signNumber: number) => {
    return SIGN_COMPOSITIONS[signNumber];
};

export const getSignGradient = (signNumber: number): string => {
    const comp = SIGN_COMPOSITIONS[signNumber];
    if (!comp) return '';

    const primaryColor = ELEMENTS[comp.primary].colors.primary;

    // Pure signs (100%) - Unicolor
    if (comp.primaryPercentage === 100) {
        return `background: ${primaryColor}`;
    }

    // Mixed signs - Gradient
    const secondaryColor = comp.secondary ? ELEMENTS[comp.secondary].colors.primary : 'transparent';

    // RTL Gradient: Primary starts from Top-Right to Bottom-Left
    return `background: linear-gradient(to bottom left, ${primaryColor} 0%, ${primaryColor} ${comp.primaryPercentage}%, ${secondaryColor} 100%)`;
};

export const getSignProgressBarGradient = (signNumber: number): string => {
    const comp = SIGN_COMPOSITIONS[signNumber];
    if (!comp) return '';

    const primaryColor = ELEMENTS[comp.primary].colors.primary;

    // Pure signs (100%) - Unicolor
    if (comp.primaryPercentage === 100) {
        return `background: ${primaryColor}`;
    }

    // Mixed signs - Gradient
    const secondaryColor = comp.secondary ? ELEMENTS[comp.secondary].colors.primary : 'transparent';

    // RTL Bar Gradient: Primary starts from Right (0%) to Left (100%)
    // Creates a smooth but distinct transition reflecting the composition
    return `background: linear-gradient(to left, ${primaryColor} 0%, ${primaryColor} ${comp.primaryPercentage - 10}%, ${secondaryColor} 100%)`;
};

export const getTurGradient = (innerSign: number, surroundingSign: number): string => {
    const innerComp = SIGN_COMPOSITIONS[innerSign];
    const outerComp = SIGN_COMPOSITIONS[surroundingSign];

    if (!innerComp || !outerComp) return '';

    const innerColor = ELEMENTS[innerComp.primary].colors.primary;
    const outerColor = ELEMENTS[outerComp.primary].colors.primary;

    // Fusion Gradient: Inner (Dominant) -> Outer
    // RTL: Starts from Top-Right
    return `background: linear-gradient(to bottom left, ${innerColor}, ${innerColor}, ${outerColor})`;
};

export const getMapGradient = (innerSign: number, surroundingSign: number): string => {
    const innerComp = SIGN_COMPOSITIONS[innerSign];
    const outerComp = SIGN_COMPOSITIONS[surroundingSign];

    if (!innerComp || !outerComp) return '';

    const innerPrimary = ELEMENTS[innerComp.primary].colors.primary;
    const outerPrimary = ELEMENTS[outerComp.primary].colors.primary;

    // Base backbone gradient
    const backbone = `linear-gradient(to bottom left, ${innerPrimary}, ${outerPrimary})`;

    const layers = [backbone];

    // Add outer secondary from left edge if exists (REVERSED - outer first)
    if (outerComp.secondary && outerComp.secondaryPercentage) {
        const outerSecondary = ELEMENTS[outerComp.secondary].colors.primary;
        // Reduced influence: divide by 1.25 for smaller radius
        const penetration = outerComp.secondaryPercentage / 1.25;
        // Radial gradient for rounder shape, lower opacity (77) for smoother integration
        layers.unshift(`radial-gradient(circle at left, ${outerSecondary}77 0%, transparent ${penetration}%)`);
    }

    // Add inner secondary from right edge if exists (REVERSED - inner second)
    if (innerComp.secondary && innerComp.secondaryPercentage) {
        const innerSecondary = ELEMENTS[innerComp.secondary].colors.primary;
        const penetration = innerComp.secondaryPercentage / 1.25;
        // Radial gradient for rounder shape, lower opacity (77) for smoother integration
        layers.unshift(`radial-gradient(circle at right, ${innerSecondary}77 0%, transparent ${penetration}%)`);
    }

    return `background: ${layers.join(', ')}`;
};

export const getElementForSign = (signNumber: number): ElementConfig => {
    const comp = SIGN_COMPOSITIONS[signNumber];
    return ELEMENTS[comp?.primary || 'fire']; // Fallback to fire
};

export const getSignTextColor = (signNumber: number): string => {
    const element = getElementForSign(signNumber);
    // Air is light (slate-200), so it needs dark text for contrast
    if (element.id === 'air') {
        return 'text-mystic-1000';
    }
    // All others (Fire, Earth, Water) are dark/saturated enough for white text
    return 'text-white';
};


